import { MergeTagNode, MergeTagPickerConfig } from './merge-tag-picker.model';

export interface MergeTagSearchOptions {
	minMatchCharLength?: number;
}

export interface MergeTagSearchResult {
	filteredTree: MergeTagNode[];
	matchedPaths: string[];
	expandedPaths: string[];
	highlights: Record<string, { label?: string; displayPath?: string }>;
}

const DEFAULT_SEARCH_OPTIONS: Required<MergeTagSearchOptions> = {
	minMatchCharLength: 2
};

export function mergeTagSearchOptionsFromConfig(config: Required<MergeTagPickerConfig>): MergeTagSearchOptions {
	return {
		minMatchCharLength: config.minSearchLength ?? DEFAULT_SEARCH_OPTIONS.minMatchCharLength
	};
}

/**
 * Determine if a value should be treated as a leaf node (non-object, non-array).
 */
export function isLeafValue(value: unknown): boolean {
	if (value === null || value === undefined) {
		return true;
	}

	if (Array.isArray(value)) {
		return false;
	}

	return typeof value !== 'object';
}

/**
 * Convert a nested object into a tree of merge tag nodes.
 */
export function buildTreeFromObject(
	data: Record<string, unknown> | null | undefined,
	parentSegments: string[] = [],
	depth: number = 0
): MergeTagNode[] {
	if (!isTraversableObject(data)) {
		return [];
	}

	return Object.keys(data)
		.sort()
		.map<MergeTagNode>(key => {
			const nextSegments = [...parentSegments, key];
			const rawValue = data[key];
			const traversable = isTraversableObject(rawValue);
			const children = traversable ? buildTreeFromObject(rawValue as Record<string, unknown>, nextSegments, depth + 1) : undefined;
			const exampleValue = traversable ? undefined : formatExampleValue(rawValue);

			// Try to extract a user-friendly label from the object if available
			const displayLabel = extractDisplayLabel(key, rawValue, parentSegments);

			return {
				key,
				label: displayLabel,
				path: nextSegments.join('.'),
				displayPath: nextSegments.join(' > '),
				depth,
				isLeaf: !traversable,
				value: traversable ? undefined : rawValue,
				exampleValue,
				children,
				parentPathSegments: parentSegments
			};
		});
}

/**
 * Flatten the merge tag tree for search processing.
 */
export function flattenTree(nodes: MergeTagNode[]): MergeTagNode[] {
	const result: MergeTagNode[] = [];

	nodes.forEach(node => {
		result.push(node);

		if (node.children?.length) {
			result.push(...flattenTree(node.children));
		}
	});

	return result;
}

/**
 * Perform a lightweight substring search across the merge tag tree and return a filtered version of the tree.
 */
export function searchMergeTags(
	query: string,
	nodes: MergeTagNode[],
	opts: MergeTagSearchOptions = {}
): MergeTagSearchResult {
	const { minMatchCharLength } = { ...DEFAULT_SEARCH_OPTIONS, ...opts };
	const normalizedQuery = query?.trim() ?? '';

	if (!normalizedQuery || normalizedQuery.length < minMatchCharLength) {
		return {
			filteredTree: cloneTree(nodes),
			matchedPaths: [],
			expandedPaths: [],
			highlights: {}
		};
	}

	const tokens = tokenizeQuery(normalizedQuery);
	if (!tokens.length) {
		return {
			filteredTree: cloneTree(nodes),
			matchedPaths: [],
			expandedPaths: [],
			highlights: {}
		};
	}

	const highlightMap: Record<string, { label?: string; displayPath?: string }> = {};
	const matchedPaths = new Set<string>();
	const expandedPaths = new Set<string>();

	flattenTree(nodes).forEach(node => {
		const label = node.label ?? '';
		const coversAllTokens = tokens.every(token => valueIncludesToken(label, token));

		if (!coversAllTokens) {
			return;
		}

		matchedPaths.add(node.path);
		collectAncestorPaths(node.path).forEach(ancestor => expandedPaths.add(ancestor));

		const target = (highlightMap[node.path] ??= {});
		const labelMatches = collectMatchIndices(label, tokens);
		if (labelMatches.length) {
			target.label = highlightText(label, labelMatches);
		}
	});

	const allowedPaths = new Set<string>([...matchedPaths, ...expandedPaths]);
	const filteredTree = filterTreeByPath(nodes, allowedPaths);

	return {
		filteredTree,
		matchedPaths: [...matchedPaths],
		expandedPaths: [...expandedPaths],
		highlights: highlightMap
	};
}

function filterTreeByPath(nodes: MergeTagNode[], allowedPaths: Set<string>): MergeTagNode[] {
	return nodes.reduce<MergeTagNode[]>((accumulator, node) => {
		const hasAccess = allowedPaths.has(node.path);
		const children = node.children?.length ? filterTreeByPath(node.children, allowedPaths) : undefined;

		if (!hasAccess && (!children || children.length === 0)) {
			return accumulator;
		}

		accumulator.push({
			...node,
			children
		});

		return accumulator;
	}, []);
}

function isTraversableObject(value: unknown): value is Record<string, unknown> {
	if (value === null || value === undefined) {
		return false;
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	return typeof value === 'object';
}

function formatExampleValue(value: unknown): string {
	if (value === null || value === undefined) {
		return '';
	}

	if (typeof value === 'string') {
		return truncate(value);
	}

	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}

	return truncate(JSON.stringify(value));
}

function truncate(value: string, maxLength: number = 50): string {
	if (value.length <= maxLength) {
		return value;
	}

	return `${value.slice(0, maxLength - 3)}...`;
}

function collectAncestorPaths(path: string): string[] {
	const segments = path.split('.');
	const ancestors: string[] = [];

	for (let i = segments.length - 1; i > 0; i--) {
		ancestors.push(segments.slice(0, i).join('.'));
	}

	return ancestors;
}

function tokenizeQuery(value: string): string[] {
	return value
		.toLowerCase()
		.split(/[\s.>/_-]+/)
		.map(token => token.trim())
		.filter(token => token.length > 0);
}

function valueIncludesToken(value: string, token: string): boolean {
	if (!token) {
		return true;
	}

	if (!value) {
		return false;
	}

	return value.toLowerCase().includes(token);
}

function collectMatchIndices(value: string, tokens: string[]): Array<[number, number]> {
	if (!value || !tokens.length) {
		return [];
	}

	const lowerValue = value.toLowerCase();
	const ranges: Array<[number, number]> = [];

	tokens.forEach(token => {
		if (!token) {
			return;
		}

		let searchIndex = 0;
		while (searchIndex <= lowerValue.length) {
			const matchIndex = lowerValue.indexOf(token, searchIndex);
			if (matchIndex === -1) {
				break;
			}

			ranges.push([matchIndex, matchIndex + token.length - 1]);
			searchIndex = matchIndex + token.length;
		}
	});

	return mergeRanges(ranges);
}

function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
	if (!ranges.length) {
		return [];
	}

	const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
	const merged: Array<[number, number]> = [];
	let [currentStart, currentEnd] = sorted[0];

	for (let i = 1; i < sorted.length; i++) {
		const [nextStart, nextEnd] = sorted[i];
		if (nextStart <= currentEnd + 1) {
			currentEnd = Math.max(currentEnd, nextEnd);
		} else {
			merged.push([currentStart, currentEnd]);
			currentStart = nextStart;
			currentEnd = nextEnd;
		}
	}

	merged.push([currentStart, currentEnd]);
	return merged;
}
function highlightText(value: string, indices: ReadonlyArray<Readonly<[number, number]>>): string {
	let result = '';
	let cursor = 0;

	for (const [start, end] of indices) {
		if (start > cursor) {
			result += escapeHtml(value.slice(cursor, start));
		}

		const matchText = escapeHtml(value.slice(start, end + 1));
		result += `<strong class="merge-tag-highlight">${matchText}</strong>`;
		cursor = end + 1;
	}

	if (cursor < value.length) {
		result += escapeHtml(value.slice(cursor));
	}

	return result;
}

function escapeHtml(value: string): string {
	return value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function cloneTree(nodes: MergeTagNode[]): MergeTagNode[] {
	return nodes.map(node => ({
		...node,
		children: node.children ? cloneTree(node.children) : undefined
	}));
}

/**
 * Extract a user-friendly display label from an object value or its key.
 * For customizations and layers collections, uses the 'label' or 'name' property if available.
 */
function extractDisplayLabel(key: string, value: unknown, parentSegments: string[]): string {
	// Check if we're inside customizations or layers objects
	const parentKey = parentSegments.length > 0 ? parentSegments[parentSegments.length - 1] : '';
	const isCustomizationsOrLayers = parentKey === 'customizations' || parentKey === 'layers';

	// If this is an object with a label or name property, use that for display
	if (isCustomizationsOrLayers && value && typeof value === 'object' && !Array.isArray(value)) {
		const obj = value as Record<string, unknown>;

		// Prefer 'label' over 'name'
		if (typeof obj.label === 'string' && obj.label.trim()) {
			return obj.label.trim();
		}

		if (typeof obj.name === 'string' && obj.name.trim()) {
			return obj.name.trim();
		}
	}

	// Default to the key itself
	return key;
}
